import * as React from 'react';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import { General, PageTitle, Section, fetchApi, generateCSVAsync, useCloneEntity } from 'gossamer-front-end';
import { BookingView, BookingViewRow } from 'cb-model';
import { doFrontEndEvaluates, getFieldArray, getFilter } from 'gossamer-unified';
import { useStore } from 'react-redux';
import { ReportMenu } from './ReportMenu';

/**
 * Booking Export Component
 *
 * Allows admins to export booking data for their institution within a date range as CSV
 */
export const BookingExport = (): React.JSX.Element => {
  const store = useStore();

  const [startDate, setStartDate] = React.useState<string>('');
  const [endDate, setEndDate] = React.useState<string>('');
  const [computedStatus, setComputedStatus] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isDownloading, setIsDownloading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [successMessage, setSuccessMessage] = React.useState<string>('');
  const [bookings, setBookings] = React.useState<BookingViewRow[]>([]);
  const BookingViewClone = useCloneEntity(BookingView, (clone) => {
    Object.values(clone.fields).forEach((field) => (field.listColumn = true)); // output all columns
    clone.fields.attendance_id.listColumn = false; // not very useful and causes loads of reference lookups
  });

  /**
   * Trigger browser download of CSV file
   */
  const downloadCSV = (csvContent: string, filename: string): void => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  /**
   * Fetch bookings from API
   */
  const handleFetch = async (): Promise<void> => {
    // Validation
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }

    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      // Build query to fetch bookings within date range
      // The API will automatically filter by institution based on admin's security context
      const queryOptions = {
        where: {
          and: [
            getFilter('start_at', 'gt', startDate + 'T00:00:00Z'),
            getFilter('start_at', 'lt', endDate + 'T23:59:59Z'),
            getFilter('computed_status', 'eq', computedStatus),
          ],
        },
        orderBy: [['start_at', 'ASC']],
        limit: 10000, // Maximum records to export
      };

      // Make API request to Gossamer backend
      const response = await fetchApi(
        'GET',
        `/main/${BookingView.id}?q=${encodeURIComponent(JSON.stringify(queryOptions))}`,
      );

      if (response.error) {
        throw new Error(response.error);
      }

      const fetchedBookings = (response.data as BookingViewRow[]).map((row) => doFrontEndEvaluates(BookingView, row));

      if (fetchedBookings.length === 0) {
        setError('No bookings found for the selected date range');
        setBookings([]);
        setIsLoading(false);
        return;
      }

      setBookings(fetchedBookings);
      setSuccessMessage(`Found ${fetchedBookings.length} booking(s)`);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch bookings');
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Download bookings as CSV file
   */
  const handleDownload = async (): Promise<void> => {
    if (bookings.length === 0) {
      setError('No bookings to download. Please fetch data first.');
      return;
    }

    setIsDownloading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Generate CSV using Gossamer utility (with automatic reference data prefetching)
      const csvContent = await generateCSVAsync(store as any, BookingViewClone, bookings, true);

      // Generate filename with date range
      const filename = `bookings_${startDate}_to_${endDate}.csv`;

      // Download
      downloadCSV(csvContent, filename);

      setSuccessMessage(`Successfully downloaded ${bookings.length} booking(s)`);
    } catch (err) {
      console.error('Error generating CSV:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate CSV');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <PageTitle title="Export Bookings" />
      <ReportMenu />

      <Section>
        <p>Export booking data for your institution within a specified date range as a CSV file.</p>

        <Form>
          <Form.Group className="mb-3" controlId="startDate">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={isLoading}
              max={endDate}
            />
            <Form.Text className="text-muted">Events starting from this date (inclusive)</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="endDate">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={isLoading}
              min={startDate}
            />
            <Form.Text className="text-muted">Events starting up to this date (inclusive)</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="computedStatus">
            <Form.Label>Booking Status</Form.Label>
            <General
              editable={true}
              fieldId="computed_status"
              field={BookingViewClone.fields.computed_status}
              value={computedStatus}
              handleChange={(val) => setComputedStatus(val)}
            />
          </Form.Group>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
              {successMessage}
            </Alert>
          )}

          <div className="d-flex gap-2">
            <Button variant="primary" onClick={handleFetch} disabled={isLoading || isDownloading}>
              {isLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  Fetching...
                </>
              ) : (
                'Fetch Bookings'
              )}
            </Button>

            <Button
              variant="success"
              onClick={handleDownload}
              disabled={isDownloading || bookings.length === 0 || isLoading}
            >
              {isDownloading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  Downloading...
                </>
              ) : (
                'Download CSV'
              )}
            </Button>
          </div>
        </Form>
      </Section>

      <Section title="Export Information">
        <ul>
          <li>Maximum 10,000 records per export</li>
          <li>Only bookings for your institution will be included</li>
          <li>Date range filters by event start date/time</li>
          <li>
            CSV includes all list columns from BookingView entity:&nbsp;
            {getFieldArray(BookingViewClone)
              .filter(([field, _fieldId]) => field.listColumn)
              .map(([field, _fieldId]) => field.label)
              .join(', ')}
          </li>
          <li>Reference fields include both ID and label columns</li>
        </ul>
        {bookings.length > 0 && (
          <Alert variant="info" className="mt-3">
            {bookings.length} booking(s) ready for export
          </Alert>
        )}
      </Section>
    </>
  );
};
